import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import {
  UiTextField,
  UiSelect,
  UiMenuItem,
  UiButton,
  UiLabel,
} from 'ui-components';
import Loading from 'components/loading/Loading';
import { usePermission } from 'hooks';
import { magicNumbers } from 'utils';
import {
  changeBulletinContentQueryValue,
  setBulletinContentQueryStep,
  createBulletinForExport,
} from 'store';
import { StyledExportBulletinContent } from './ExportBulletinContent.styles';

const ExportBulletinContentView = ({
  showCount = true,
  hasError,
  setHasError,
}) => {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const { userPermission } = usePermission();
  const activeStep = useSelector(
    (state) => state.bulletinContentQueryReducer.activeStep
  );
  const count = useSelector((state) => state.bulletinContentQueryReducer.count);
  const selectedBulletinId = useSelector(
    (state) => state.bulletinContentQueryReducer.selectedBulletinId
  );
  const newBulletinTitle = useSelector(
    (state) => state.bulletinContentQueryReducer.newBulletinTitle
  );
  const bulletinList = useSelector(
    (state) => state.bulletinContentQueryReducer.bulletinList
  );
  const createBulletin = useSelector(
    (state) => state.bulletinContentQueryReducer.createBulletin
  );

  const { permission: permiAddNewBulletin } = useMemo(
    () => userPermission('Bulletin', 'store'),
    [userPermission]
  );

  const { int_value_1: bulletinCountLimit, permission: permiBulletinCount } =
    useMemo(
      () => userPermission('Variables', 'bulletin_count'),
      [userPermission]
    );

  const canCreateNewBulletin =
    permiAddNewBulletin &&
    permiBulletinCount &&
    bulletinList.data?.length < bulletinCountLimit;

  const handleCountChange = (event) => {
    const { value } = event.target;
    if (value?.indexOf(0) === 0) return;

    const numericValue = parseInt(value, 10);

    const hasError =
      value && numericValue > magicNumbers.MAX_BULLETIN_CONTENT_QUERY_COUNT;

    setHasError(hasError);

    dispatch(
      changeBulletinContentQueryValue({
        key: 'count',
        value,
      })
    );
  };

  const handleCreateBulletin = () => {
    dispatch(createBulletinForExport(newBulletinTitle));
  };

  if (bulletinList.loading) {
    return <Loading spinning />;
  }

  if (activeStep === 'createBulletin') {
    return (
      <StyledExportBulletinContent>
        <div className='field-group'>
          <UiLabel className='field-label'>
            {t('content:bulletinExport.bulletinName')}
          </UiLabel>
          <UiTextField
            value={newBulletinTitle}
            onChange={(event) =>
              dispatch(
                changeBulletinContentQueryValue({
                  key: 'newBulletinTitle',
                  value: event.target.value,
                })
              )
            }
            inputProps={{
              'data-testid': 'export-bulletin-content-name-input',
            }}
          />
        </div>
        <UiButton
          variant='contained'
          disabled={!newBulletinTitle || createBulletin.loading}
          loading={createBulletin.loading}
          onClick={handleCreateBulletin}
          data-testid='export-bulletin-content-create-btn'
        >
          {t('content:bulletinExport.createBulletin')}
        </UiButton>
      </StyledExportBulletinContent>
    );
  }

  return (
    <StyledExportBulletinContent>
      {showCount && (
        <div className='field-group'>
          <UiLabel className='field-label'>
            {t('content:contentsCount')}
          </UiLabel>
          <UiTextField
            placeholder={t('content:countDescription')}
            value={count}
            onChange={handleCountChange}
            onKeyDown={(event) => {
              if (
                event.key === '.' ||
                event.key === '-' ||
                event.key === '+' ||
                event.key === 'e' ||
                event.key === 'E'
              ) {
                event.preventDefault();
              }
            }}
            style={{ width: 250 }}
            inputProps={{ min: 1, type: 'number' }}
            data-testid='export-bulletin-content-count-input'
          />
          {hasError && (
            <div className='max-count-hint'>
              {t('content:bulletinExport.maxContents', {
                maxCount: magicNumbers.MAX_BULLETIN_CONTENT_QUERY_COUNT,
              })}
            </div>
          )}
        </div>
      )}

      <div className='field-group'>
        <UiLabel className='field-label'>
          {t('content:bulletinExport.selectBulletin')}
        </UiLabel>
        <UiSelect
          fullWidth
          value={selectedBulletinId || ''}
          onChange={(event) =>
            dispatch(
              changeBulletinContentQueryValue({
                key: 'selectedBulletinId',
                value: event.target.value,
              })
            )
          }
          inputProps={{
            'data-testid': 'export-bulletin-content-bulletin-select',
          }}
        >
          {bulletinList.data?.map((item, index) => (
            <UiMenuItem
              key={item.id}
              value={item.id}
              data-testid={`export-bulletin-content-bulletin-option-${index}`}
            >
              {item.title}
            </UiMenuItem>
          ))}
        </UiSelect>
      </div>

      {canCreateNewBulletin && (
        <UiButton
          variant='text'
          className='create-bulletin-link'
          onClick={() =>
            dispatch(setBulletinContentQueryStep('createBulletin'))
          }
          data-testid='export-bulletin-content-create-new-btn'
        >
          {t('content:bulletinExport.createNewBulletin')}
        </UiButton>
      )}
    </StyledExportBulletinContent>
  );
};

ExportBulletinContentView.propTypes = {
  showCount: PropTypes.bool,
  hasError: PropTypes.bool,
  setHasError: PropTypes.func,
};

export default ExportBulletinContentView;
